import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "checkbox", "row",
    "copyLinkBtn", "sendEmailBtn",
    "formStep", "resultStep",
    "formActions", "resultActions",
    "linkInput", "linkContainer", "copyBtn",
    "successMessage"
  ];

  static values = {
    url: String
  };

  connect() {
    this._boundReset = this._reset.bind(this);
    this.element.addEventListener("hidden.bs.modal", this._boundReset);
    this.updateSubmitState();
  }

  disconnect() {
    this.element.removeEventListener("hidden.bs.modal", this._boundReset);
  }

  updateSubmitState() {
    const hasChecked = this.checkboxTargets.some(cb => cb.checked);
    this.copyLinkBtnTarget.disabled = !hasChecked;
    this.sendEmailBtnTarget.disabled = !hasChecked;
  }

  async generateLink() {
    await this._createEditLink("copy_link");
  }

  async sendEmail() {
    await this._createEditLink("send_email");
  }

  copyLink(event) {
    event.preventDefault();
    const url = this.linkInputTarget.value;
    if (!url) return;

    navigator.clipboard.writeText(url).then(() => {
      this.copyBtnTarget.textContent = "Copied!";
      setTimeout(() => {
        this.copyBtnTarget.textContent = "Copy";
      }, 1500);
    }).catch(() => {
      // Fallback
      this.linkInputTarget.select();
      document.execCommand("copy");
    });
  }

  // Private

  async _createEditLink(actionType) {
    const formData = this._collectFormData(actionType);
    if (!formData) return;

    this._setLoading(true);

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        this._showResult(data, actionType);
      } else {
        alert(data.errors?.join(", ") || "An error occurred.");
        this._setLoading(false);
      }
    } catch (error) {
      alert("An error occurred. Please try again.");
      this._setLoading(false);
    }
  }

  _collectFormData(actionType) {
    const checkedBoxes = this.checkboxTargets.filter(cb => cb.checked);
    if (checkedBoxes.length === 0) return null;

    const requirementIds = checkedBoxes.map(cb => cb.value);

    const accessLevel = this.element.querySelector('input[name="access_level"]')?.value || "applicant";
    const notifyOnSubmission = this.element.querySelector('input[name="notify_on_submission"]')?.checked ? "1" : "0";

    // Collect requirement versions for changed requirements
    const requirementVersions = {};
    requirementIds.forEach(id => {
      const versionRadio = this.element.querySelector(`input[name="requirement_versions[${id}]"]:checked`);
      if (versionRadio) {
        requirementVersions[id] = versionRadio.value;
      }
    });

    return {
      requirement_ids: requirementIds,
      access_level: accessLevel,
      notify_on_submission: notifyOnSubmission,
      requirement_versions: requirementVersions,
      action_type: actionType
    };
  }

  _showResult(data, actionType) {
    this.formStepTarget.classList.add("d-none");
    this.resultStepTarget.classList.remove("d-none");
    this.formActionsTarget.classList.add("d-none");
    this.resultActionsTarget.classList.remove("d-none");

    if (actionType === "send_email") {
      this.successMessageTarget.textContent = data.message || "Edit link sent to the applicant via email.";
      this.successMessageTarget.classList.remove("d-none");
      this.linkContainerTarget.classList.add("d-none");
    } else {
      this.linkInputTarget.value = data.url;
      this.successMessageTarget.textContent = "Edit link created. Copy the link below.";
      this.successMessageTarget.classList.remove("d-none");
      this.linkContainerTarget.classList.remove("d-none");
      // Auto-copy to clipboard
      navigator.clipboard.writeText(data.url).catch(() => {});
    }
  }

  _setLoading(loading) {
    this.copyLinkBtnTarget.disabled = loading;
    this.sendEmailBtnTarget.disabled = loading;
  }

  _reset() {
    // Uncheck all requirement checkboxes
    this.checkboxTargets.forEach(cb => cb.checked = false);

    // Reset version radios back to "old"
    this.element.querySelectorAll('input[name^="requirement_versions["]').forEach(radio => {
      radio.checked = radio.value === "old";
    });

    // Reset access_level select
    const accessInput = this.element.querySelector('input[name="access_level"]');
    if (accessInput) {
      accessInput.value = "applicant";
      const label = accessInput.closest(".app-select")?.querySelector('[data-app-select-target="label"]');
      if (label) label.textContent = "Only the Applicant";
    }

    // Uncheck notify checkbox
    const notify = this.element.querySelector('input[name="notify_on_submission"]');
    if (notify) notify.checked = false;

    // Show form step, hide result step
    this.formStepTarget.classList.remove("d-none");
    this.resultStepTarget.classList.add("d-none");
    this.formActionsTarget.classList.remove("d-none");
    this.resultActionsTarget.classList.add("d-none");

    // Clear result fields
    this.linkInputTarget.value = "";
    this.successMessageTarget.textContent = "";
    this.successMessageTarget.classList.add("d-none");
    this.linkContainerTarget.classList.remove("d-none");
    this.copyBtnTarget.textContent = "Copy";

    this.updateSubmitState();
  }
}
